import { startTransition, useCallback, useEffect, useMemo } from 'react';
import {
  getSelectedElementId,
  getSelectedVersion,
  useSelectedVersion,
  useUIState,
} from '@client/store/ui';
import { useResolvedVersion } from '@client/hooks/use-resolved-version.hooks';
import { URL_CATEGORY_NONE } from '@client/models/router.model';
import { shouldIgnoreKeyboardShortcut } from '@shared/helpers/event.helpers';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import {
  AMP_BASELINE_FROM_PROPOSAL,
  AMP_PROPOSAL_ADD,
  AMP_PROPOSAL_CHANGE_NAME,
  AMP_PROPOSAL_DELETE,
  AMP_PROPOSAL_DUPLICATE,
} from '@shared/constants';
import {
  filterProposalsByTargetedElement,
  getAffectedElementsIdsByProposal,
  getProposals,
  getProposal,
} from '@shared/helpers/proposals';
import {
  OneOfElementListElements,
  OneOfElements,
} from '@shared/models/project.model';
import type { IProposal } from '@shared/models/proposals.model';
import type {
  ItemOrItemId,
  SemiPartial,
} from '@shared/models/type-helpers.model';
import {
  buildProposalEffectiveColorIndexMap,
  getProposalSwatchColor,
} from '@shared/helpers/proposal.helpers';
import { getId } from '@shared/helpers/object.helpers';
import { createId } from '@shared/helpers/id.helpers';
import { BASELINE_COLOR } from '@shared/helpers/proposals/proposal-colors.constants';
import {
  NEW_VERSION_CREATED_SNACKBAR_MESSAGE,
  useAddVersion,
  useProject,
} from '@client/store/project';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import { findFreeName } from '@shared/helpers/string.helpers';
import {
  useSelectedProposalId,
  useSetSelectedProposalId,
} from '@client/hooks/use-selected-proposal-id.hooks';
import { getSelectedProposalIdAfterDelete } from '../helpers/proposal-delete-selection.helpers';
import { usePvgisEnvironmentHourData } from '@client/store/pvgis/pvgis.hook';
import { useDataTables } from '@features/data-table/store/data-table-store.hooks';
import { getEmptyFrozenArray } from '@shared/helpers/array.helpers';
import {
  buildBaselineVersionFromProposal,
  hasClonedProposalResults,
} from '@shared/helpers/actions/create-baseline-from-proposal.helpers';
import { areAllProposalActionIdsPresent } from '@shared/helpers/pipeline/mutators/duplicate-proposal.mutator';
import type { ResolvedVersionEnrichContext } from '@shared/helpers/actions/resolved-version-enrich-cache.helpers';
import amplitudeLog from '@client/amplitude';
/**
 * Get the proposals from the selected version.
 * @returns IProposal[]
 */
export const useProposals = (): IProposal[] => {
  const version = useSelectedVersion();
  return useMemo(() => getProposals(version), [version]);
};

/**
 * {@link IProposal} rows whose actions reference the element (same closest-version rule as v1 `useProposalsUsingElement`).
 */
export const useProposalsUsingElement = (
  element: OneOfElementListElements,
): IProposal[] => {
  const version = useSelectedVersion();

  return useMemo(
    () => filterProposalsByTargetedElement(version, element),
    [version, element],
  );
};

/**
 * Resolves the {@link IProposal} for a given id from the selected version.
 * Returns `undefined` when not found.
 */
export const useProposal = (
  proposalOrId?: ItemOrItemId<IProposal>,
): IProposal | undefined => {
  const proposals = useProposals();
  return useMemo(
    () => getProposal(proposals, proposalOrId),
    [proposals, proposalOrId],
  );
};

/**
 * Resolves the currently selected {@link IProposal} from the selected version,
 * driven by {@link useSelectedProposalId} (URL-backed). Returns `undefined`
 * when no proposal is selected (baseline view) or when the version does not
 * support action-based proposals.
 */
export const useSelectedProposal = (): IProposal | undefined => {
  const version = useSelectedVersion();
  const selectedProposalId = useSelectedProposalId();
  return useMemo(
    () => getProposal(version, selectedProposalId),
    [version, selectedProposalId],
  );
};

/**
 * Hex color for a {@link IProposal} swatch/bar. Mirrors the resolution used by
 * {@link ProposalBar} so swatches in the overview, element rows and charts stay
 * in sync (uses {@link buildProposalEffectiveColorIndexMap} for legacy colorIndex 0
 * rows, then {@link getProposalSwatchColor}). Returns {@link BASELINE_COLOR}
 * when no proposal is provided or it can't be resolved.
 */
export const useProposalColor = (
  proposalOrId?: ItemOrItemId<IProposal>,
): string => {
  const version = useSelectedVersion();
  const proposals = useProposals();
  const id = proposalOrId ? getId(proposalOrId) : undefined;
  return useMemo(() => {
    if (!id) return BASELINE_COLOR;
    const proposal = getProposal(version, id);
    if (!proposal) return BASELINE_COLOR;
    const effective = buildProposalEffectiveColorIndexMap(proposals);
    return getProposalSwatchColor(effective.get(id) ?? proposal.colorIndex);
  }, [id, version, proposals]);
};

/**
 * Element-level bar color for proposal mode:
 *  - {@link BASELINE_COLOR} when no proposal is selected
 *  - {@link BASELINE_COLOR} when a proposal is selected but neither the element
 *    nor any of its descendants are affected by it
 *    (see {@link getAffectedElementsIdsByProposal})
 *  - the selected proposal's color (see {@link useProposalColor}) when the
 *    element itself, any of its children, or any ancestor chain is affected
 *
 * `includeParentIds: true` ensures that parent and grandparent rows in the
 * element tree also receive the proposal colour when a child is targeted.
 */
export const useElementSelectedProposalColor = (
  elementOrId: ItemOrItemId<OneOfElements>,
): string => {
  // Walk the resolved version so children that only exist in the proposal-resolved tree
  // (e.g. added by a proposal-scoped ElementRecipeApply, see DEV-3293) inherit the proposal color
  // via the downward propagation in {@link getAffectedElementsIdsByProposal}.
  const version = useResolvedVersion();
  const proposal = useSelectedProposal();
  const elementId = getId(elementOrId);
  const proposalColor = useProposalColor(proposal?.id);

  const affectedElementsIds = useMemo<ReadonlySet<string>>(
    () =>
      proposal
        ? getAffectedElementsIdsByProposal(version, proposal, true)
        : new Set<string>(),
    [version, proposal],
  );

  return useMemo(() => {
    if (!proposal) return BASELINE_COLOR;
    return affectedElementsIds.has(elementId) ? proposalColor : BASELINE_COLOR;
  }, [proposal, affectedElementsIds, elementId, proposalColor]);
};

/**
 * Group-level variant of {@link useElementSelectedProposalColor}: returns the
 * selected proposal's color when the proposal affects **any** of the given
 * `itemIds` (or any of their descendants), otherwise {@link BASELINE_COLOR}.
 * Falls back to baseline when no proposal is selected.
 *
 * Uses `includeParentIds: true` so that group rows show the proposal colour
 * even when only a child element inside the group is targeted.
 */
export const useItemsSelectedProposalColor = (
  itemIds: readonly string[],
): string => {
  // Same rationale as {@link useElementSelectedProposalColor}: resolve the version so
  // proposal-only descendants are visible to the affected-id walk (DEV-3293).
  const version = useResolvedVersion();
  const proposal = useSelectedProposal();
  const proposalColor = useProposalColor(proposal?.id);

  const affectedElementsIds = useMemo<ReadonlySet<string>>(
    () =>
      proposal
        ? getAffectedElementsIdsByProposal(version, proposal, true)
        : new Set<string>(),
    [version, proposal],
  );

  return useMemo(() => {
    if (!proposal) return BASELINE_COLOR;
    const anyAffected = itemIds.some((id) => affectedElementsIds.has(id));
    return anyAffected ? proposalColor : BASELINE_COLOR;
  }, [proposal, affectedElementsIds, itemIds, proposalColor]);
};

/**
 * Returns a callback to rename a {@link IProposal}.
 *
 * Reads the current proposal list from {@link getSelectedVersion} at call time
 * (instead of via {@link useProposals}) so that callers like {@link ProposalBar}
 * don't have to subscribe every bar to the whole proposal list just to dedupe a
 * name. Dispatches a single {@link ProjectChangeType.ProposalUpdate} change so
 * the pipeline runs once. Logs to Amplitude on success.
 */
export const useRenameProposal = () => {
  const applyChanges = useApplyProjectChanges();

  return useCallback(
    async (id: string, requestedName: string): Promise<void> => {
      const version = getSelectedVersion(true);
      const proposals = getProposals(version);
      const availableName = findFreeName(
        proposals.map((p) => p.name),
        requestedName,
      );
      await applyChanges(
        [
          {
            type: ProjectChangeType.ProposalUpdate,
            versionId: version.id,
            updates: [{ id, name: availableName }],
          },
        ],
        {
          amplitude: {
            eventType: AMP_PROPOSAL_CHANGE_NAME,
            eventProperties: { ProposalId: id },
          },
        },
      );
    },
    [applyChanges],
  );
};

/**
 * Returns a callback to reorder {@link IProposal} rows in the selected version.
 * Pass the desired id order; the baseline is always rendered first in the UI
 * and is not part of proposals, so all current proposal ids must be present.
 */
export const useReorderProposals = () => {
  const applyChanges = useApplyProjectChanges();

  return useCallback(
    async (orderedIds: string[]): Promise<void> => {
      const version = getSelectedVersion(true);
      await applyChanges([
        {
          type: ProjectChangeType.ProposalReorder,
          versionId: version.id,
          orderedIds,
        },
      ]);
    },
    [applyChanges],
  );
};

export const useUpdateProposals = () => {
  const applyChanges = useApplyProjectChanges();

  return useCallback(
    (...updates: SemiPartial<IProposal, 'id'>[]) => {
      const version = getSelectedVersion(true);
      return applyChanges([
        {
          type: ProjectChangeType.ProposalUpdate,
          versionId: version.id,
          updates,
        },
      ]);
    },
    [applyChanges],
  );
};

export const useDeleteProposal = () => {
  const applyChanges = useApplyProjectChanges();
  const selectedProposalId = useSelectedProposalId();
  const setSelectedProposalId = useSetSelectedProposalId();

  return useCallback(
    async (proposalOrId: ItemOrItemId<IProposal>) => {
      const version = getSelectedVersion(true);
      const proposalId = getId(proposalOrId);
      const deletingSelectedProposal = selectedProposalId === proposalId;
      if (deletingSelectedProposal) {
        setSelectedProposalId(
          getSelectedProposalIdAfterDelete(
            getProposals(version),
            proposalId,
            selectedProposalId,
          ),
        );
      }
      const result = await applyChanges([
        {
          type: ProjectChangeType.ProposalDelete,
          versionId: version.id,
          proposalId,
        },
      ]);
      if (result.cancelled && deletingSelectedProposal) {
        setSelectedProposalId(proposalId);
      }
    },
    [applyChanges, selectedProposalId, setSelectedProposalId],
  );
};

/**
 * Returns a callback that appends a new {@link IProposal} to the selected
 * version via a {@link ProjectChangeType.ProposalAdd} change and selects the
 * newly created proposal (matches v1 {@link useAddProposal} behaviour, which
 * also auto-selects the new proposal). Logs {@link AMP_PROPOSAL_ADD} to
 * Amplitude after a successful update.
 *
 * When `defaults` is omitted: if a proposal is selected (URL), that row seeds
 * {@link addProposal} except its name, color and {@link IProposal.resultsRecord}.
 * The mutator assigns the next automatic A/B/C name and clears action ids
 * (DEV-3268); results always start from the selected version's baseline until
 * proposal-specific compute runs. Pass explicit `defaults` to override.
 *
 * The new proposal id is generated before the project update so Proposal
 * Overview can mark the future row selected before the optimistic store write
 * causes its first render. URL-backed content is updated in a transition.
 *
 * Feedback runs through the built-in apply-layer snackbar
 * ({@link ApplyProjectChangesSnackbarOptions} `type: 'undo'`): global undo drops the
 * new proposal and `onAfterUndo` restores the selection the user had before.
 */
export const useAddProposal = (
  onOptimisticSelectionChange?: (
    proposalId: IProposal['id'] | undefined,
  ) => void,
) => {
  const applyChanges = useApplyProjectChanges();
  const setSelectedProposalId = useSetSelectedProposalId();
  const selectedProposalId = useSelectedProposalId();

  return useCallback(
    async (defaults?: Partial<IProposal>): Promise<void> => {
      const version = getSelectedVersion(true);

      const source =
        defaults === undefined && selectedProposalId
          ? getProposal(version, selectedProposalId)
          : undefined;

      let resolvedDefaults: Partial<IProposal> | undefined;
      if (defaults !== undefined) {
        resolvedDefaults = defaults;
      } else if (source !== undefined) {
        // Omit colorIndex so addProposal always assigns a conflict-free color; keep baseline
        // rollup for the new bar (not the source proposal’s computed results).
        const {
          name: _sourceName,
          colorIndex: _colorIndex,
          resultsRecord: _sourceResults,
          ...sourceRest
        } = source;
        resolvedDefaults = {
          ...sourceRest,
          resultsRecord: version.resultsRecord ?? {},
        };
      } else {
        // Baseline view: show version rollup on the new bar until proposal-specific compute runs
        resolvedDefaults = { resultsRecord: version.resultsRecord ?? {} };
      }

      const newProposalId = createId({});
      const restorePreviousSelection = () => {
        onOptimisticSelectionChange?.(undefined);
        setSelectedProposalId(selectedProposalId);
      };

      // The overview-only state is urgent. URL selection drives the heavier
      // element list and may follow without delaying the bar's first paint.
      onOptimisticSelectionChange?.(newProposalId);
      startTransition(() => setSelectedProposalId(newProposalId));

      const applyResult = await applyChanges(
        [
          {
            type: ProjectChangeType.ProposalAdd,
            versionId: version.id,
            proposalId: newProposalId,
            defaults: resolvedDefaults,
          },
        ],
        {
          amplitude: AMP_PROPOSAL_ADD,
          onEnrichFailed: restorePreviousSelection,
          snackbar: {
            type: 'undo',
            message: 'New proposal created',
            onAfterUndo: () => setSelectedProposalId(selectedProposalId),
          },
        },
      );

      const addedProposalExists = getProposals(getSelectedVersion()).some(
        ({ id }) => id === newProposalId,
      );
      if (applyResult.cancelled || !addedProposalExists) {
        restorePreviousSelection();
        return;
      }
    },
    [
      applyChanges,
      onOptimisticSelectionChange,
      setSelectedProposalId,
      selectedProposalId,
    ],
  );
};

/**
 * Returns a callback that duplicates an {@link IProposal} via
 * {@link ProjectChangeType.ProposalDuplicate}: cloned actions (new ids), copied
 * results/report fields, inserted after the source. Selects the copy afterwards.
 *
 * Enrichment is skipped when {@link hasClonedProposalResults} is true and every
 * source {@link IProposal.actionIds} entry exists on the version — the mutator
 * already copied {@link IProposal.resultsRecord} and cloned the same action set.
 * An empty record, or dangling action ids, falls through to the ProposalDuplicate
 * enrich path so the copy is not shown with KPIs its cloned actions cannot reproduce.
 */
export const useDuplicateProposal = () => {
  const applyChanges = useApplyProjectChanges();
  const setSelectedProposalId = useSetSelectedProposalId();
  const selectedProposalId = useSelectedProposalId();

  return useCallback(
    async (proposalOrId: ItemOrItemId<IProposal>): Promise<void> => {
      const version = getSelectedVersion(true);
      const sourceId = getId(proposalOrId);
      const source = getProposal(version, sourceId);
      const previousIds = new Set(getProposals(version).map(getId));

      const applyResult = await applyChanges(
        [
          {
            type: ProjectChangeType.ProposalDuplicate,
            versionId: version.id,
            proposalId: sourceId,
          },
        ],
        {
          amplitude: {
            eventType: AMP_PROPOSAL_DUPLICATE,
            eventProperties: { ProposalId: sourceId },
          },
          // Skip enrich only when cloned KPIs match a fully resolved action list.
          skipEnrichment:
            source !== undefined &&
            hasClonedProposalResults(source) &&
            areAllProposalActionIdsPresent(version, source.actionIds),
          snackbar: {
            type: 'undo',
            message: 'Proposal duplicated',
            onAfterUndo: () => setSelectedProposalId(selectedProposalId),
          },
        },
      );

      if (applyResult.cancelled) return;
      const updatedVersion = getSelectedVersion();
      const newProposal = getProposals(updatedVersion).find(
        (p) => !previousIds.has(p.id),
      );
      if (newProposal) setSelectedProposalId(newProposal.id);
    },
    [applyChanges, setSelectedProposalId, selectedProposalId],
  );
};

/**
 * Returns a callback that promotes an {@link IProposal} into a brand-new baseline
 * version: the proposal is merged onto the version it lives on (the resolved tree,
 * see {@link buildBaselineVersionFromProposal}) and added as a fresh version that
 * starts with one empty proposal, named via {@link getNextVersionName}.
 *
 * Mirrors {@link useAddProposal}: a single {@link useApplyProjectChanges} commit
 * (a declarative {@link ProjectChangeType.VersionAdd} from {@link useAddVersion}, activated
 * in the same commit) with the built-in snackbar and global undo. Add and activation share
 * one undo frame, so Undo drops the whole version and the undo itself navigates back to the
 * version the user came from ({@link IUndoRedoState.selectedVersionId}) — for the snackbar
 * button and the keyboard shortcut alike.
 *
 * {@link useAddVersion} navigates to the new version as soon as it is in the local
 * store (the server PUT may still be in flight) and, with `hintRename`, sets
 * `renamingId` so the proposal overview version switcher opens the name field focused.
 *
 * Enrichment is skipped when {@link hasClonedProposalResults} is true: the factory
 * already carries the selected proposal's {@link IProposal.resultsRecord}, and
 * {@link addVersionToProject} remaps those keys onto the new element ids. An empty
 * record falls through to the VersionAdd enrich path.
 */
export const useCreateBaselineFromProposal = () => {
  const addVersion = useAddVersion();
  const project = useProject();
  const environmentHourData = usePvgisEnvironmentHourData();
  const dataTables = useDataTables();
  const { setRenamingId } = useUIState('setRenamingId');

  return useCallback(
    async (proposal: IProposal): Promise<void> => {
      const version = getSelectedVersion(true);

      const enrichContext: ResolvedVersionEnrichContext = {
        project,
        weatherHours: environmentHourData ?? getEmptyFrozenArray(),
        dataTables,
      };

      const factory = buildBaselineVersionFromProposal(
        version,
        proposal,
        enrichContext,
      );

      const result = await addVersion(factory, {
        hintRename: true,
        applyOptions: {
          // Cloned + remapped proposal results are already valid; do not recompute.
          skipEnrichment: hasClonedProposalResults(proposal),
          snackbar: {
            type: 'undo',
            message: NEW_VERSION_CREATED_SNACKBAR_MESSAGE,
            // The undo restores the version selection; only the rename hint is ours to clear
            onAfterUndo: () => setRenamingId(undefined),
          },
        },
      });

      // A cancelled or failed add is already reported by the apply layer
      if (result.status !== 'added') return;

      amplitudeLog(AMP_BASELINE_FROM_PROPOSAL, {
        VersionId: result.element.id,
        ProposalId: proposal.id,
      });
    },
    [addVersion, project, environmentHourData, dataTables, setRenamingId],
  );
};

/**
 * Registers Cmd+Backspace (Mac) / Ctrl+Backspace (Windows) to delete the
 * selected proposal when no element is selected and the event does not
 * originate from an input or overlay.
 */
export const useDeleteProposalShortcut = (): void => {
  const applyChanges = useApplyProjectChanges();
  const selectedProposalId = useSelectedProposalId();
  const setSelectedProposalId = useSetSelectedProposalId();
  const isReadonly = useIsReadonly();
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Backspace') return;
      if (!e.metaKey && !e.ctrlKey) return;
      if (isReadonly) return;
      if (shouldIgnoreKeyboardShortcut(e)) return;
      if (!selectedProposalId) return;
      // Read the raw selection state during keydown so proposal deletion never follows a normal element delete.
      const selectedElementIdAtKeyDown = getSelectedElementId();
      if (
        selectedElementIdAtKeyDown &&
        selectedElementIdAtKeyDown !== URL_CATEGORY_NONE
      )
        return;

      e.preventDefault();
      e.stopPropagation();

      const version = getSelectedVersion(true);
      if ((version.proposals?.length ?? 0) <= 1) return;
      const proposalId = selectedProposalId;
      const nextProposalId = getSelectedProposalIdAfterDelete(
        getProposals(version),
        proposalId,
        selectedProposalId,
      );

      setSelectedProposalId(nextProposalId);

      void applyChanges(
        [
          {
            type: ProjectChangeType.ProposalDelete,
            versionId: version.id,
            proposalId,
          },
        ],
        {
          snackbar: {
            type: 'undo',
            message: 'Proposal deleted',
            onAfterUndo: () => {
              setSelectedProposalId(proposalId);
            },
          },
          amplitude: {
            eventType: AMP_PROPOSAL_DELETE,
            eventProperties: { ProposalId: proposalId },
          },
        },
      );
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isReadonly, selectedProposalId, setSelectedProposalId, applyChanges]);
};
